// =============================================================================
// process-test-run — the batch runner (process-tests P7)
// =============================================================================
//
// `runTests(model, opts)` is the shared core every surface (CLI, LSP, panel)
// calls. It is a thin batch wrapper over `evalTest` (process-test-eval.ts): it
// collects the model's `test` declarations, resolves each to its process under
// test, evaluates it, and aggregates the per-test verdicts + (optionally) step
// coverage into a {@link TestReport}. Pure — no IO, no reference resolution
// beyond target lookup; the model must already be resolved + cascaded.

import type { Diagnostic, QualifiedName } from "./types.js";
import type { ResolvedModel, ResolvedModule } from "./resolver.js";
import type { ProcessModel } from "./process-model.js";
import type { TestDecl } from "./v010-ast.js";
import { evalTest, type TestResult } from "./process-test-eval.js";
import { enumerateTraces, type EnumerateOptions } from "./process-trace-enumerator.js";
import { choiceCoverage, type ChoiceCoverage } from "./process-choice-coverage.js";
import { walkProcess } from "./process-walker.js";
import { walkerContextFor } from "./process-contexts.js";
import type { ProcessTestResolver } from "./process-test-canon.js";

/** Bounding + reporting knobs. `maxLoop`/`traceCap` are the {@link EnumerateOptions}
 *  the CLI exposes as `--maxLoop`/`--traceCap`; `coverage` opts into the (more
 *  expensive) per-process step-coverage pass. Left open to a future timing env
 *  (P4) without a signature change. */
export interface RunTestsOptions {
  readonly maxLoop?: number;
  readonly traceCap?: number;
  readonly coverage?: boolean;
}

/** A test's bucketed verdict. `errored` = could not be evaluated (no resolvable
 *  target, or an `error`-severity `test.*` diagnostic — unmatched/ambiguous pin,
 *  untestable process); it is distinct from a clean `failed` assertion. */
export type TestRunStatus = "passed" | "failed" | "open" | "errored";

/** One test's run outcome. `result` is absent only when the target could not be
 *  resolved (no evaluation happened). `diagnostics` folds the run-level target
 *  diagnostic (if any) together with `result.diagnostics`. */
export interface TestRunResult {
  readonly name: string;
  readonly id?: string;
  readonly ownerPath?: QualifiedName;
  /** The resolved process name, or the written `of <Target>` when unresolved. */
  readonly target: string;
  /** The resolved process's fully-qualified path (`[...ownerPath, name]`) —
   *  distinguishes same-named processes under different owners. Absent when the
   *  target didn't resolve. */
  readonly targetPath?: QualifiedName;
  readonly status: TestRunStatus;
  readonly result?: TestResult;
  readonly diagnostics: readonly Diagnostic[];
}

/** Step coverage for one process — the fraction of its statically-reachable leaf
 *  steps exercised by at least one of its tests' pinned trace-sets. `total` is a
 *  single all-branches walk (so `select`/`parallel` cases unrun by a default
 *  still count); `pct` is `maxLoop`-bounded and clamped to [0,100]. */
export interface ProcessCoverage {
  readonly process: string;
  readonly covered: number;
  readonly total: number;
  readonly pct: number;
}

export interface CoverageReport {
  readonly perProcess: readonly ProcessCoverage[];
  readonly overall: { readonly covered: number; readonly total: number; readonly pct: number };
}

/** The PRIMARY coverage metric (design §11): choice-point coverage — which
 *  decision outcomes (arms, loop 0/1/many, select run-sets, parallel branches +
 *  merge-fail) the tests exercised. Governance (`@tested` / a coverage gate)
 *  reasons about THIS, not step coverage, which is the secondary lens. */
export interface ChoiceCoverageReport {
  readonly perProcess: readonly ChoiceCoverage[];
  readonly overall: { readonly covered: number; readonly total: number; readonly pct: number };
}

export interface TestReport {
  readonly results: readonly TestRunResult[];
  /** Choice-point coverage — the headline §11 metric. Present when `opts.coverage`. */
  readonly choiceCoverage?: ChoiceCoverageReport;
  /** Step coverage — the secondary lens (which leaf steps ran). Present when `opts.coverage`. */
  readonly coverage?: CoverageReport;
  readonly passed: number;
  readonly failed: number;
  readonly open: number;
  readonly errored: number;
  /** A gate signal: no failing and no errored tests. OPEN does NOT flip `ok`
   *  (unbounded-loop processes are legitimately OPEN); the CLI promotes OPEN to
   *  a failure only under `--strict`. */
  readonly ok: boolean;
}

/** Gather every `test` declaration reachable from a resolved model: the top-level
 *  + `in`-placed tests hoisted onto `model.tests`, plus module-body tests on each
 *  `ResolvedModule.body.tests` (recursively). */
export function collectTests(model: ResolvedModel): readonly TestDecl[] {
  const out: TestDecl[] = [...(model.tests ?? [])];
  const visit = (m: ResolvedModule): void => {
    if (m.body.tests) out.push(...m.body.tests);
    for (const c of m.modules) visit(c);
  };
  for (const m of model.modules) visit(m);
  return out;
}

/** Match a test's `of <Target>` against the process list. A full path
 *  (`Plat.Booking`) or a bare tail (`Booking`) both resolve; a bare target also
 *  matches a process's own name. */
function resolveTarget(
  target: QualifiedName | undefined,
  processes: readonly ProcessModel[],
): ProcessModel | undefined {
  if (!target || target.length === 0) {
    // No `of` clause: unambiguous only when the model has exactly one process.
    return processes.length === 1 ? processes[0] : undefined;
  }
  const want = target.join(".");
  for (const p of processes) {
    const full = [...(p.ownerPath ?? []), p.name].join(".");
    if (full === want || p.name === want) return p;
  }
  // Suffix match (`Booking` ↔ `Plat.Booking`) — last resort, first wins.
  for (const p of processes) {
    const full = [...(p.ownerPath ?? []), p.name].join(".");
    if (full.endsWith(`.${want}`)) return p;
  }
  return undefined;
}

/** Distinct leaf-step nodeIds an all-branches walk of `process` reaches — the
 *  coverage denominator. Uses the same `p:<id>/…` nodeId scheme the enumerated
 *  traces carry (producer leaves are byte-identical to `walkProcess`). */
function reachableLeafNodes(process: ProcessModel, model: ResolvedModel): Set<string> {
  const ctx = walkerContextFor(model, process);
  const walk = walkProcess(
    process,
    { branchSelection: new Map(), expandedSubprocesses: new Set() },
    ctx,
    { showAllBranches: true },
  );
  return new Set(walk.steps.map((s) => s.nodeId));
}

/**
 * Run every `test` in the model. Each is resolved to its process, evaluated via
 * {@link evalTest}, and bucketed. When `opts.coverage`, a per-process step
 * coverage pass runs over the union of each process's tests' trace-sets.
 */
export function runTests(model: ResolvedModel, opts: RunTestsOptions = {}): TestReport {
  const enumOpts: EnumerateOptions = {
    // The resolved model is what builds the LatencyEnv the P4 race resolver +
    // time budgets fold against (declared `latency` fields); omitting it starves
    // them to a trivial missing-latency env.
    model,
    ...(opts.maxLoop !== undefined ? { maxLoop: opts.maxLoop } : {}),
    ...(opts.traceCap !== undefined ? { traceCap: opts.traceCap } : {}),
  };
  const processes = model.processModels ?? [];
  const tests = collectTests(model);

  const results: TestRunResult[] = [];
  // Per process: the union of leaf nodeIds its tests' trace-sets reach (coverage
  // numerator). Keyed by ProcessModel identity.
  const coveredByProcess = new Map<ProcessModel, Set<string>>();
  // Per process: the tests targeting it — the choice-coverage input.
  const testsByProcess = new Map<ProcessModel, TestDecl[]>();

  for (const test of tests) {
    const proc = resolveTarget(test.target, processes);
    const targetLabel = proc
      ? proc.name
      : test.target
        ? test.target.join(".")
        : "(no target)";
    const common = {
      name: test.name,
      ...(test.id ? { id: test.id } : {}),
      ...(test.ownerPath ? { ownerPath: test.ownerPath } : {}),
      target: targetLabel,
    };

    if (!proc) {
      results.push({
        ...common,
        status: "errored",
        diagnostics: [
          {
            severity: "error",
            message: test.target
              ? `Test '${test.name}': no process matches target '${test.target.join(".")}'.`
              : `Test '${test.name}': no target (\`of <Process>\`) and the model has ${processes.length === 0 ? "no" : "multiple"} processes.`,
            code: "test.no-target",
            ...(test.targetSource ? { source: test.targetSource } : test.nameSource ? { source: test.nameSource } : {}),
          },
        ],
      });
      continue;
    }

    const ctx = walkerContextFor(model, proc);
    const result = evalTest(proc, { pins: test.pins, assertions: test.assertions }, ctx, enumOpts);
    const targetPath: QualifiedName = [...(proc.ownerPath ?? []), proc.name];
    results.push({ ...common, targetPath, status: bucket(result), result, diagnostics: result.diagnostics });

    if (opts.coverage) {
      // Re-enumerate to read the trace-set leaf nodeIds (evalTest discards them).
      const en = enumerateTraces(proc, test.pins, ctx, enumOpts);
      let set = coveredByProcess.get(proc);
      if (!set) {
        set = new Set();
        coveredByProcess.set(proc, set);
      }
      for (const tr of en.traces) for (const s of tr.steps) set.add(s.nodeId);
      (testsByProcess.get(proc) ?? testsByProcess.set(proc, []).get(proc)!).push(test);
    }
  }

  let coverage: CoverageReport | undefined;
  if (opts.coverage) {
    const perProcess: ProcessCoverage[] = [];
    let cAll = 0;
    let tAll = 0;
    for (const [proc, covered] of coveredByProcess) {
      const total = reachableLeafNodes(proc, model);
      // Intersect: a pinned test may reach a leaf the default all-branches walk
      // elides only in pathological cases — clamp defensively.
      let hit = 0;
      for (const id of covered) if (total.has(id)) hit++;
      const totalN = total.size;
      const pct = totalN === 0 ? 100 : Math.min(100, Math.round((hit / totalN) * 100));
      perProcess.push({ process: proc.name, covered: hit, total: totalN, pct });
      cAll += hit;
      tAll += totalN;
    }
    perProcess.sort((a, b) => a.process.localeCompare(b.process));
    coverage = {
      perProcess,
      overall: { covered: cAll, total: tAll, pct: tAll === 0 ? 100 : Math.min(100, Math.round((cAll / tAll) * 100)) },
    };
  }

  // Choice-point coverage (the §11 headline) — per process, the fraction of its
  // choice-space cells its tests exercised.
  let choice: ChoiceCoverageReport | undefined;
  if (opts.coverage) {
    const perProcess: ChoiceCoverage[] = [];
    let cAll = 0;
    let tAll = 0;
    for (const [proc, tsts] of testsByProcess) {
      const cc = choiceCoverage(proc, tsts, walkerContextFor(model, proc), enumOpts);
      perProcess.push(cc);
      cAll += cc.covered;
      tAll += cc.total;
    }
    perProcess.sort((a, b) => a.process.localeCompare(b.process));
    choice = {
      perProcess,
      overall: { covered: cAll, total: tAll, pct: tAll === 0 ? 100 : Math.round((cAll / tAll) * 100) },
    };
  }

  const passed = results.filter((r) => r.status === "passed").length;
  const failed = results.filter((r) => r.status === "failed").length;
  const open = results.filter((r) => r.status === "open").length;
  const errored = results.filter((r) => r.status === "errored").length;

  return {
    results,
    ...(choice ? { choiceCoverage: choice } : {}),
    ...(coverage ? { coverage } : {}),
    passed,
    failed,
    open,
    errored,
    ok: failed === 0 && errored === 0,
  };
}

/** Bucket one {@link TestResult} into a run status. An `error`-severity
 *  diagnostic (unmatched/ambiguous pin, untestable process) means the test could
 *  not be meaningfully evaluated → `errored`; otherwise a failing assertion wins
 *  over an open one over a clean pass. */
function bucket(result: TestResult): TestRunStatus {
  if (result.diagnostics.some((d) => d.severity === "error")) return "errored";
  if (result.perAssertion.some((a) => a.status === "fail")) return "failed";
  if (result.perAssertion.some((a) => a.status === "open") || result.open.length > 0) return "open";
  return "passed";
}

// =============================================================================
// Governance getters — @tested / @testCoverage (process-tests P9)
// =============================================================================

/**
 * A {@link ProcessTestResolver} over a whole model: runs the batch suite (with
 * choice-coverage) ONCE, lazily on first use, and indexes each process's pass
 * state + coverage by its qualified key. Mirrors `buildProcessLatencyResolver`.
 * A non-process key → undefined (neutral). A suite failure degrades every
 * process to neutral (untested / 0%) rather than taking down the policy run.
 */
export function buildProcessTestResolver(model: ResolvedModel): ProcessTestResolver {
  const procKeys = new Set<string>();
  for (const pm of model.processModels ?? []) {
    if (pm.name) procKeys.add([...(pm.ownerPath ?? []), pm.name].join("."));
  }
  const testedByPath = new Map<string, boolean>();
  const coverageByName = new Map<string, number>();
  let built = false;
  const build = (): void => {
    if (built) return;
    built = true;
    try {
      const r = runTests(model, { coverage: true });
      for (const res of r.results) {
        if (!res.targetPath) continue;
        const k = res.targetPath.join(".");
        testedByPath.set(k, (testedByPath.get(k) ?? false) || res.status === "passed");
      }
      for (const c of r.choiceCoverage?.perProcess ?? []) coverageByName.set(c.process, c.pct);
    } catch {
      // Degrade to neutral for every subject — a suite crash must not fail the
      // whole policy/diagnostic run (mirrors the latency resolver's guard).
    }
  };
  return (subjectKey: string) => {
    if (!procKeys.has(subjectKey)) return undefined;
    build();
    const tail = subjectKey.split(".").pop() ?? subjectKey;
    return { tested: testedByPath.get(subjectKey) ?? false, testCoverage: coverageByName.get(tail) ?? 0 };
  };
}

// =============================================================================
// Regression gate — base-pass / head-fail (process-tests P9)
// =============================================================================

/** A test that PASSED in base but no longer passes in head — the model drift
 *  broke a modelled expectation (the §11 regression signal). */
export interface TestRegression {
  readonly name: string;
  readonly id?: string;
  readonly target: string;
  readonly baseStatus: TestRunStatus;
  readonly headStatus: TestRunStatus;
}

/** Stable cross-version identity of a test: its owner-scoped `#id` when present,
 *  else owner + name + target. Anchors the base→head regression match (and the
 *  spec §9 diff — the same key the node universe stamps a `test` element with). */
export function testIdentity(r: Pick<TestRunResult, "id" | "name" | "ownerPath" | "target">): string {
  const owner = (r.ownerPath ?? []).join(".");
  return r.id ? `${owner}#${r.id}` : `${owner}::${r.name}::${r.target}`;
}

/**
 * Compare two test runs (base → head) and return the regressions: tests that
 * passed in base and now `failed` / `errored` in head. A test present on only
 * one side (added / removed coverage) is not a regression — only drift that
 * broke an expectation the author had already pinned green.
 */
export function testRegressions(base: TestReport, head: TestReport): TestRegression[] {
  const headByIdentity = new Map(head.results.map((r) => [testIdentity(r), r]));
  const out: TestRegression[] = [];
  for (const b of base.results) {
    if (b.status !== "passed") continue;
    const h = headByIdentity.get(testIdentity(b));
    if (!h) continue; // removed in head — not a regression
    if (h.status === "failed" || h.status === "errored") {
      out.push({
        name: h.name,
        ...(h.id ? { id: h.id } : {}),
        target: h.target,
        baseStatus: b.status,
        headStatus: h.status,
      });
    }
  }
  return out;
}
